import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { OrganizationSchema, WebsiteSchema, HomePageSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import { sportCategories, newsArticles } from '@/mocks/homeData';

const articleImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Champions%20League%20football%20stadium%20with%20dramatic%20floodlights%20and%20players%20celebrating%20on%20the%20pitch%2C%20epic%20atmosphere%20with%20confetti%20and%20crowd%20cheering%2C%20professional%20sports%20photography%20with%20warm%20golden%20tones&width=800&height=450&seq=cat-news-01&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=NBA%20basketball%20game%20intense%20moment%20two%20players%20facing%20off%20on%20the%20court%2C%20arena%20crowd%20energy%2C%20dramatic%20sports%20photography%20with%20motion%20and%20spotlights&width=800&height=450&seq=cat-news-02&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Chinese%20womens%20volleyball%20team%20celebrating%20victory%20on%20the%20court%2C%20dynamic%20action%20shot%20with%20athletes%20jumping%20and%20spiking%2C%20stadium%20atmosphere%20with%20red%20colors&width=800&height=450&seq=cat-news-03&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Formula%20One%20racing%20car%20speeding%20on%20track%20with%20motion%20blur%2C%20dramatic%20overcast%20sky%2C%20asphalt%20texture%20and%20speed%20sensation&width=800&height=450&seq=cat-news-04&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Esports%20arena%20with%20massive%20LED%20screens%20and%20packed%20audience%2C%20gaming%20competition%20stage%20with%20dramatic%20cyan%20lighting&width=800&height=450&seq=cat-news-05&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Professional%20swimmer%20in%20competition%20pool%20doing%20breaststroke%2C%20water%20splashes%20and%20intense%20motion%2C%20olympic%20stadium%20atmosphere&width=800&height=450&seq=cat-news-06&orientation=landscape',
};

const categoryLabels: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  esports: '电竞',
  racing: '赛车',
  tennis: '网球',
  swimming: '游泳',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = sportCategories.find((c) => c.slug === slug) || sportCategories[0];
  const filteredArticles = newsArticles.filter((a) => a.category === (slug || 'football'));
  const displayArticles = filteredArticles.length > 0 ? filteredArticles : newsArticles;

  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
      <HomePageSchema />
      <BreadcrumbSchema items={[
        { name: '首页', url: '/' },
        { name: currentCategory.name, url: `/category/${slug}` },
      ]} />
      <Navbar />
      <main className="pt-16">
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium">{currentCategory.name}</span>
              </nav>

              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{currentCategory.name}资讯</h1>
                  <p className="text-sm text-foreground-500 mt-0.5">共 {displayArticles.length} 篇相关文章</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {sportCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-200/70 hover:border-primary-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {displayArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={articleImages[article.id]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-3 left-3 bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-medium px-2.5 py-1 rounded-full">
                        {categoryLabels[article.category] || article.category}
                      </span>
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-base font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span><i className="ri-time-line mr-1"></i>{article.date}</span>
                        <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}